import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { buildVisitorWebchatIdentity } from 'modules/webchat/domain/helpers';
import {
  makeCreateWebchatLeadUseCase,
  makeIdentifyWebchatVisitorUseCase,
} from 'modules/webchat/domain/useCases';
import {
  WEBCHAT_AUTHENTICATION_ROUTES,
  WEBCHAT_LEAD_LINK_MASKED_ROUTES,
} from 'modules/webchat/presentation/constants';
import { useWebchat } from 'modules/webchat/presentation/contexts/WebchatContext';
import {
  buildWebchatLeadLink,
  buildWebchatLoginReturnPath,
  PRE_CHAT_INITIAL_STATE,
  transitionPreChat,
} from 'modules/webchat/presentation/helpers';
import { IPreChatFormValues } from 'modules/webchat/presentation/validations';
import { makeStorageProvider } from 'shared/infra/providers';
import {
  COUNTRY_CODES,
  ECOMMERCE_UNIT_IDS,
} from 'shared/presentation/constants';
import {
  useLocale,
  useNavigation,
  useRoute,
} from 'shared/presentation/contexts';
import { useRequestErrorHandler } from 'shared/presentation/hooks';

type TParams = {
  /** Chamado quando o pré-chat termina: chat aberto ou ida para o login. */
  onFinish(): void;
};

export default function usePreChat({ onFinish }: TParams) {
  const { language } = useLocale('webchat');
  const { handleError } = useRequestErrorHandler('webchat');
  const navigation = useNavigation();
  const { pathname, search } = useRoute();
  const { status, mountWebchat, openWebchat } = useWebchat();

  const [state, dispatch] = useReducer(
    transitionPreChat,
    PRE_CHAT_INITIAL_STATE,
  );

  const sessionStorageProvider = useMemo(
    () => makeStorageProvider({ type: 'SESSION' }),
    [],
  );

  // Descarta a resposta de um envio antigo ou que chegou depois de fechar o modal.
  const attempt = useRef(0);
  const isActive = useRef(true);
  const openRequested = useRef(false);
  const onFinishRef = useRef(onFinish);

  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  useEffect(
    () => () => {
      isActive.current = false;
    },
    [],
  );

  const submit = useCallback(
    async ({ name, phone, terms }: IPreChatFormValues) => {
      attempt.current += 1;

      const currentAttempt = attempt.current;
      const isCurrent = () =>
        isActive.current && currentAttempt === attempt.current;
      const countryCode = COUNTRY_CODES[language];

      dispatch({ type: 'SUBMITTED' });

      try {
        const identifiedType =
          await makeIdentifyWebchatVisitorUseCase().execute({ phone });

        if (!isCurrent()) return;

        dispatch({ type: 'IDENTIFIED', visitorType: identifiedType });

        if (identifiedType === 'cliente') return;

        let visitorType = identifiedType;

        if (identifiedType === 'novo') {
          visitorType = await makeCreateWebchatLeadUseCase().execute({
            name,
            phone,
            countryCode,
            unitId: ECOMMERCE_UNIT_IDS[language],
            terms,
            link: buildWebchatLeadLink(
              window.location,
              WEBCHAT_LEAD_LINK_MASKED_ROUTES,
            ),
          });

          if (!isCurrent()) return;

          dispatch({
            type:
              visitorType === 'novo'
                ? 'LEAD_CREATED'
                : 'LEAD_ALREADY_REGISTERED',
          });
        }

        mountWebchat(
          buildVisitorWebchatIdentity({
            name,
            phone,
            countryCode,
            type: visitorType,
          }),
        );
      } catch (error) {
        if (!isCurrent()) return;

        dispatch({ type: 'FAILED' });
        handleError(error);
      }
    },
    [handleError, language, mountWebchat],
  );

  useEffect(() => {
    if (
      state.step !== 'openingChat' ||
      status === 'idle' ||
      openRequested.current
    ) {
      return;
    }

    openRequested.current = true;

    // O openWebchat espera o widget ficar pronto e avisa a pessoa se ele falhar.
    openWebchat().finally(() => {
      if (isActive.current) onFinishRef.current();
    });
  }, [openWebchat, state.step, status]);

  const goToLogin = useCallback(() => {
    const returnPath = buildWebchatLoginReturnPath(
      { pathname, search },
      WEBCHAT_AUTHENTICATION_ROUTES,
    );

    // Sem página de volta, o login cai na área do cliente e o chat não abre sozinho.
    if (returnPath) {
      try {
        sessionStorageProvider.store('WEBCHAT_OPEN_AFTER_LOGIN', {
          path: returnPath.split('?')[0],
          requestedAt: Date.now(),
        });
      } catch {
        // Sem storage, o cliente volta logado e abre o chat pelo launcher.
      }
    }

    // encodeURIComponent: com encodeURI, o primeiro & da query cortava o origin.
    navigation.push(
      '/login',
      returnPath ? { origin: encodeURIComponent(returnPath) } : undefined,
    );
    onFinishRef.current();
  }, [navigation, pathname, search, sessionStorageProvider]);

  const restart = useCallback(() => dispatch({ type: 'RESTARTED' }), []);

  const loading = state.step === 'identifying' || state.step === 'creatingLead';

  return {
    step: state.step,
    loading,
    isFormVisible: state.step === 'form' || loading,
    submit,
    goToLogin,
    restart,
  };
}
